import React, { useState } from 'react';
import styled from 'styled-components';

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  min-height: 80px;
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;

const Label = styled.label`
  font-weight: 500;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const RequiredIndicator = styled.span`
  color: #e53e3e;
  margin-left: 0.25rem;
`;

interface Field {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'textarea';
  required: boolean;
  options?: string[];
  enabled: boolean;
}

interface RegistrationConfig {
  enabled: boolean;
  fields: Field[];
}

interface Props {
  onSubmit: (formData: Record<string, any>) => void;
  onClose: () => void;
  registrationConfig?: RegistrationConfig;
}

export const RegistrationDialog: React.FC<Props> = ({ onSubmit, onClose, registrationConfig }) => {
  // Initialize state for all form fields
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  // Default fields if no custom configuration is provided
  const defaultFields: Field[] = [
    { id: 'age', name: 'age', label: 'Age', type: 'number', required: true, enabled: true },
    { id: 'gender', name: 'gender', label: 'Gender', type: 'select', required: true, options: ['male', 'female', 'other'], enabled: true },
    { id: 'shortBio', name: 'shortBio', label: 'Short Bio', type: 'textarea', required: false, enabled: true },
    { id: 'whoAmI', name: 'whoAmI', label: 'Who Am I', type: 'select', required: false, enabled: true },
    { id: 'interests', name: 'interests', label: 'Interests', type: 'textarea', required: false, enabled: true }
  ];

  // Get the fields to display
  const getFieldsToDisplay = (): Field[] => {
    if (!registrationConfig || !registrationConfig.enabled) {
      return defaultFields;
    }
    return registrationConfig.fields.filter(field => field.enabled);
  };

  const fieldsToDisplay = getFieldsToDisplay();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormValues(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formValues);
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={e => e.stopPropagation()}>
        <h2>Register to Join</h2>
        <Form onSubmit={handleSubmit}>
          {fieldsToDisplay.map(field => (
            <FormGroup key={field.id}>
              <Label>
                {field.label}
                {field.required && <RequiredIndicator>*</RequiredIndicator>}
              </Label>
              
              {field.type === 'text' && (
                <Input
                  type="text"
                  name={field.name}
                  value={formValues[field.name] || ''}
                  onChange={handleInputChange}
                  required={field.required}
                />
              )}
              
              {field.type === 'textarea' && (
                <TextArea
                  name={field.name}
                  value={formValues[field.name] || ''}
                  onChange={handleInputChange}
                  required={field.required}
                />
              )}
              
              {field.type === 'number' && (
                <Input
                  type="number"
                  name={field.name}
                  min="18"
                  max="100"
                  value={formValues[field.name] || ''}
                  onChange={handleInputChange}
                  required={field.required}
                />
              )}
              
              {field.type === 'select' && (
                <Select
                  name={field.name}
                  value={formValues[field.name] || ''}
                  onChange={handleInputChange}
                  required={field.required}
                >
                  <option value="">Select {field.label}</option>
                  {field.options?.map(option => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </Select>
              )}
              
              {field.type === 'checkbox' && (
                <Input
                  type="checkbox"
                  name={field.name}
                  checked={formValues[field.name] || false}
                  onChange={handleInputChange}
                  required={field.required}
                />
              )}
            </FormGroup>
          ))}
          
          <Button type="submit">Join Wheel</Button>
        </Form>
      </DialogContent>
    </DialogOverlay>
  );
}; 